import React, { useRef } from 'react'
import { SERVICE_TITLE } from 'service'
import { featureFlags } from 'config/feature-flags'
import GoogleAuthUploadPanel from './GoogleAuthUploadPanel'
import { PRESETS } from './importReducer'
import { matchesAcceptedExt, toBrowserAccept } from './importUtils'
import styles from './style.module.scss'

// v2.5: UploadIcon 제거 (빈 드롭존에서 아이콘 미표시 — Figma 기준)

const FileIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M4 3h8l4 4v10a1 1 0 01-1 1H4a1 1 0 01-1-1V4a1 1 0 011-1z"
      stroke="#1B3F6E"
      strokeWidth="1.5"
    />
    <path d="M12 3v4h4" stroke="#1B3F6E" strokeWidth="1.5" />
  </svg>
)

const ImportUploadStep = ({
  preset,
  fileName,
  dragging,
  dispatch,
  templateSchemas = {},
  onRetryTemplate,
  scannedQrs = [],
  t,
}) => {
  const fileRef = useRef()

  const handlePreset = (p) => {
    dispatch({ type: 'SET_PRESET', payload: p })
  }

  const handleRetry = (e, presetId) => {
    e.stopPropagation() // 카드 선택 막음
    if (onRetryTemplate) onRetryTemplate(presetId)
  }

  const handleFile = (file) => {
    if (!file) return
    const accepted = preset.acceptedExts || [preset.ext]
    if (!matchesAcceptedExt(file.name, accepted)) {
      // eslint-disable-next-line no-alert
      alert(
        t('record.import.v2.onlyFileAllowed', {
          ext: accepted.map((e) => e.toUpperCase().slice(1)).join(', '),
        }),
      )
      return
    }
    dispatch({ type: 'SET_FILE', payload: { file, fileName: file.name } })
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    dispatch({ type: 'SET_DRAGGING', payload: true })
  }

  const handleDragLeave = () => {
    dispatch({ type: 'SET_DRAGGING', payload: false })
  }

  const handleDrop = (e) => {
    e.preventDefault()
    dispatch({ type: 'SET_DRAGGING', payload: false })
    handleFile(e.dataTransfer.files[0])
  }

  return (
    <div className={styles.uploadStep}>
      <p className={styles.uploadDesc}>{t('record.import.v2.uploadDesc')}</p>

      <div>
        <p className={styles.sectionTitle}>{t('record.import.v2.serviceSelect')}</p>
        <div className={styles.presetList}>
          {PRESETS.filter(
            (p) =>
              p.id !== 'googleauth' ||
              (featureFlags.totpAuthenticator && featureFlags.totpGoogleImport),
          ).map((p) => {
            const schemaState = templateSchemas[p.id]
            const hasTemplateError = p.id !== 'officepassword' && schemaState?.status === 'error'
            return (
              <div
                key={p.id}
                className={[styles.presetItem, preset.id === p.id ? styles.presetItemActive : '']
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => handlePreset(p)}
                role="radio"
                aria-checked={preset.id === p.id}
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') handlePreset(p)
                }}
              >
                <div
                  className={[
                    styles.presetRadio,
                    preset.id === p.id ? styles.presetRadioActive : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {preset.id === p.id && <div className={styles.presetRadioDot} />}
                </div>
                <div className={styles.presetContent}>
                  <div className={styles.presetHeader}>
                    <span className={styles.presetLabel}>
                      {t(p.labelKey, { service: SERVICE_TITLE })}
                    </span>
                    <span
                      className={[
                        styles.presetBadge,
                        preset.id === p.id ? styles.presetBadgeActive : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {p.ext.toUpperCase().slice(1)}
                    </span>
                  </div>
                  <p className={styles.presetDesc}>{t(p.descKey, { service: SERVICE_TITLE })}</p>
                  {hasTemplateError && (
                    <p className={styles.presetTemplateError}>
                      {t('record.import.v2.template.loadError')}
                      <button
                        type="button"
                        className={styles.presetRetryBtn}
                        onClick={(e) => handleRetry(e, p.id)}
                      >
                        {t('record.import.v2.template.retry')}
                      </button>
                    </p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {preset.id === 'googleauth' ? (
        <div>
          <p className={styles.sectionTitle}>{t('record.totp.import.sectionTitle')}</p>
          <GoogleAuthUploadPanel
            preset={preset}
            scannedQrs={scannedQrs}
            dispatch={dispatch}
            t={t}
          />
        </div>
      ) : (
        <div>
          <p className={styles.sectionTitle}>{t('record.import.v2.fileUpload')}</p>
          <div
            className={[styles.dropZone, dragging ? styles.dropZoneActive : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => fileRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === 'Enter') fileRef.current?.click()
            }}
          >
            {fileName ? (
              <>
                <div className={styles.dropZoneIconFile}>
                  <FileIcon />
                </div>
                <p className={styles.dropZoneFileName}>{fileName}</p>
                <p className={styles.dropZoneHint}>{t('record.import.v2.changeFile')}</p>
              </>
            ) : (
              <>
                <p className={styles.dropZoneText}>{t('record.import.v2.dragOrClick')}</p>
                <p className={styles.dropZoneHint}>
                  {t('record.import.v2.allowedFormat', {
                    ext: (preset.acceptedExts || [preset.ext]).join(', '),
                  })}
                </p>
              </>
            )}
          </div>
          <input
            type="file"
            ref={fileRef}
            accept={toBrowserAccept(preset.acceptedExts || [preset.ext])}
            style={{ display: 'none' }}
            onChange={(e) => handleFile(e.target.files[0])}
          />
        </div>
      )}
    </div>
  )
}

export default ImportUploadStep
